import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { classifyCommand } from './mqttCommandLabels';

interface MqttLogRow {
  id: string;
  imei: string;
  topic: string;
  direction: string;
  commandId: string | null;
  isBinary: boolean;
  rawPayload: string;
  bytes: number;
  receivedAt: string;
  deviceId: string | null;
  deviceName: string | null;
  tenantName: string | null;
  /** Bloques ya decodificados por el backend (ver decodeXiaomaBlocks). */
  sensors: Array<{ port: number; type: string; metric: string | null; raw: number; value: number | null }> | null;
}

const PAGE_SIZE = 100;
const POLL_MS = 10_000;

const th: React.CSSProperties = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' };
const td: React.CSSProperties = { padding: '11px 16px', fontSize: 13 };

export default function MqttLogsPage() {
  const { t, i18n } = useTranslation();
  const [rows, setRows]       = useState<MqttLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [imei, setImei]       = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [viewRow, setViewRow] = useState<MqttLogRow | null>(null);

  // Ref del filtro actual — el poll de abajo necesita leerlo sin quedar
  // atado a `imei` como dependencia (si no, cada tecleo reinicia el timer).
  const imeiRef = useRef(imei);
  imeiRef.current = imei;

  const load = useCallback(async (currentImei: string) => {
    try {
      setError('');
      const { data } = await api.get<MqttLogRow[]>('/admin/mqtt-logs', {
        params: { limit: PAGE_SIZE, imei: currentImei || undefined },
      });
      setRows(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      setError(t('admin.mqtt.logs.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    load(imei);
  }, [imei, load]);

  // Auto-refresh liviano — solo recarga la primera página. Si el usuario ya
  // pidió "cargar más", el poll siguiente la reinicia a la página 1; es una
  // herramienta de debug, no necesita preservar scroll infinito exacto.
  useEffect(() => {
    const interval = setInterval(() => load(imeiRef.current), POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    try {
      const { data } = await api.get<MqttLogRow[]>('/admin/mqtt-logs', {
        params: { limit: PAGE_SIZE, imei: imei || undefined, before: last.id },
      });
      setRows(r => [...r, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      setError(t('admin.mqtt.logs.loadError'));
    }
  };

  return (
    <div className="page">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('admin.mqtt.logs.title')}</h1>
        <p style={{ fontSize: 13, color: 'var(--gray)' }}>{t('admin.mqtt.logs.subtitle')}</p>
      </div>

      <input
        value={imei}
        onChange={e => setImei(e.target.value.trim())}
        placeholder={t('admin.mqtt.logs.filterPlaceholder')}
        style={{ marginBottom: 16, padding: '8px 12px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, width: 260, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}
      />

      {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>{t('admin.mqtt.logs.empty')}</div>
      ) : (
        <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={th}>{t('admin.mqtt.logs.columns.time')}</th>
                <th style={th}>{t('admin.mqtt.logs.columns.device')}</th>
                <th style={th}>{t('admin.mqtt.logs.columns.command')}</th>
                <th style={th}>{t('admin.mqtt.logs.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const cmd = classifyCommand(row.commandId, row.isBinary);
                return (
                  <tr key={row.id} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td style={{ ...td, color: 'var(--gray)', whiteSpace: 'nowrap' }}>
                      {new Date(row.receivedAt).toLocaleString(i18n.language)}
                    </td>
                    <td style={td}>
                      {/* La condición es `deviceId`, NO `deviceName`: un device
                          auto-registrado por MQTT entra sin label, así que
                          mirar el nombre marcaba como "sin asignar" a devices
                          que SÍ estaban asignados a un tenant y decodificando
                          bien — justo el caso del piloto. Sin label se muestra
                          el IMEI, que es el identificador que igual existe. */}
                      {row.deviceId ? (
                        <div>
                          <div style={{ fontWeight: 500, ...(row.deviceName ? {} : { fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace', fontSize: 12 }) }}>
                            {row.deviceName ?? row.imei}
                          </div>
                          {row.tenantName && <div style={{ fontSize: 11, color: 'var(--gray)' }}>{row.tenantName}</div>}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: '#f5f5f3', color: 'var(--gray)', fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
                          {t('admin.mqtt.logs.unassigned', { imei: row.imei })}
                        </span>
                      )}
                    </td>
                    {/* Resumen compacto: un frame puede traer 30+ bloques y
                        listarlos todos convertía cada fila en un muro de texto.
                        Acá va el conteo y, en rojo, los tipos que la tabla del
                        protocolo no conoce — que es lo único accionable de un
                        vistazo. El desglose completo vive en el modal. */}
                    <td style={td}>
                      {row.sensors && row.sensors.length > 0 ? (() => {
                        const desconocidos = [...new Set(
                          row.sensors.filter(b => !b.metric).map(b => b.type)
                        )];
                        const conocidos = [...new Set(
                          row.sensors.filter(b => b.metric).map(b => b.metric as string)
                        )];
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div>
                              <span>📊</span>{' '}
                              <span style={{ fontWeight: 600 }}>{row.sensors.length}</span>
                              <span style={{ color: 'var(--gray)' }}> {t('admin.mqtt.logs.readings')}</span>
                            </div>
                            {conocidos.length > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--gray)', lineHeight: 1.4 }}>
                                {conocidos.slice(0, 4).join(', ')}
                                {conocidos.length > 4 && ` +${conocidos.length - 4}`}
                              </div>
                            )}
                            {desconocidos.length > 0 && (
                              <div style={{
                                fontSize: 11, fontWeight: 600, alignSelf: 'flex-start',
                                padding: '2px 8px', borderRadius: 99,
                                background: 'var(--red-bg, #fdecea)', color: 'var(--red, #c0392b)',
                              }}>
                                ❓ {desconocidos.join(', ')}
                              </div>
                            )}
                          </div>
                        );
                      })() : (
                        <><span>{cmd.emoji}</span> {cmd.label}</>
                      )}
                    </td>
                    <td style={td}>
                      <button
                        onClick={() => setViewRow(row)}
                        style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
                      >
                        {t('admin.mqtt.logs.viewJson')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && rows.length > 0 && (
        <button
          onClick={loadMore}
          style={{ marginTop: 14, padding: '8px 16px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
        >
          {t('admin.mqtt.logs.loadMore')}
        </button>
      )}

      {viewRow && <RawPayloadModal row={viewRow} onClose={() => setViewRow(null)} />}
    </div>
  );
}

function formatPayload(row: MqttLogRow): string {
  if (row.isBinary) return row.rawPayload;
  try {
    return JSON.stringify(JSON.parse(row.rawPayload), null, 2);
  } catch {
    return row.rawPayload;
  }
}

function RawPayloadModal({ row, onClose }: { row: MqttLogRow; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{t('admin.mqtt.logs.jsonModal.title')}</h2>
        <p style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>{row.topic}</p>
        {row.isBinary && <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 12 }}>{t('admin.mqtt.logs.jsonModal.binaryNote')}</p>}

        {/* Desglose legible primero; el hex queda abajo como respaldo. */}
        {row.sensors && row.sensors.length > 0 && (
          <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', maxHeight: '45vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10, color: 'var(--gray)', fontWeight: 600 }}>{t('admin.mqtt.logs.jsonModal.port')}</th>
                  <th style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10, color: 'var(--gray)', fontWeight: 600 }}>{t('admin.mqtt.logs.jsonModal.sensor')}</th>
                  <th style={{ padding: '7px 12px', textAlign: 'right', fontSize: 10, color: 'var(--gray)', fontWeight: 600 }}>{t('admin.mqtt.logs.jsonModal.value')}</th>
                </tr>
              </thead>
              <tbody>
                {row.sensors.map((b, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)', background: b.metric ? undefined : 'var(--red-bg, #fdecea)' }}>
                    <td style={{ padding: '6px 12px', color: 'var(--gray)' }}>p{b.port}</td>
                    <td style={{ padding: '6px 12px', fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
                      {b.metric ?? <span style={{ color: 'var(--red, #c0392b)', fontWeight: 600 }}>❓ {b.type}</span>}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {b.metric ? b.value : <span style={{ color: 'var(--red, #c0392b)' }}>{b.raw}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <pre style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px',
          fontSize: 12, lineHeight: 1.6, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '8px 0 0',
        }}>
          {formatPayload(row)}
        </pre>

        <button onClick={onClose} className="primary-button" style={{ width: '100%', marginTop: 20 }}>
          {t('admin.mqtt.logs.jsonModal.close')}
        </button>
      </div>
    </div>
  );
}
